import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import { LockClosedIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function SetPassword({ isReset = false }) {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  
  // Password validation
  const validations = [
    { label: 'At least 8 characters', valid: password.length >= 8 },
    { label: 'Contains uppercase letter', valid: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter', valid: /[a-z]/.test(password) },
    { label: 'Contains number', valid: /\d/.test(password) },
    { label: 'Passwords match', valid: password && password === confirmPassword }
  ];
  
  const allValid = validations.every(v => v.valid);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const endpoint = isReset ? '/auth/reset-password' : '/auth/set-password';
      const response = await api.post(endpoint, { token, password });
      
      toast.success(isReset ? 'Password reset successfully!' : 'Password set successfully!');
      
      // Auto-login if tokens are returned
      if (response.data.accessToken) {
        localStorage.setItem('neoshift-auth', JSON.stringify({
          state: {
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken
          }
        }));
        window.location.href = '/dashboards';
      } else {
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!token) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-white mb-4">
          Invalid link
        </h2>
        <p className="text-surface-400 mb-6">
          This link is invalid or has expired.
        </p>
        <a href="/login" className="btn-primary">
          Back to login
        </a>
      </div>
    );
  }
  
  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-display font-bold text-white mb-2">
        {isReset ? 'Reset your password' : 'Set your password'}
      </h2>
      <p className="text-surface-400 mb-8">
        {isReset 
          ? 'Enter your new password below.'
          : 'Create a secure password for your account.'}
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-surface-300 mb-2">
            Password
          </label>
          <div className="relative">
            <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-surface-300 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
        
        {/* Password requirements */}
        <div className="bg-surface-800/50 rounded-lg p-4 space-y-2">
          {validations.map((validation, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              {validation.valid ? (
                <CheckIcon className="w-4 h-4 text-green-400" />
              ) : (
                <XMarkIcon className="w-4 h-4 text-surface-500" />
              )}
              <span className={validation.valid ? 'text-green-400' : 'text-surface-500'}>
                {validation.label}
              </span>
            </div>
          ))}
        </div>
        
        <button
          type="submit"
          disabled={isLoading || !allValid}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            isReset ? 'Reset Password' : 'Set Password'
          )}
        </button>
      </form>
    </div>
  );
}

